import React, { useEffect, useState } from "react";
import { Button, notification } from "antd";
import { isChunkLoadError, isReloadInProgress, reloadOnce } from "@/utils/buildCheck";
import { useBuildCheck, useReloadOnRouteChange } from "@/hooks/useBuildCheck";
import UpdateAvailableBanner from "@/components/UpdateAvailableBanner";
export const CHUNK_LOAD_FAILED_NOTIFICATION_KEY = "pd3751-chunk-load-failed";
const getHistory = () => window.g_history;
const getPathname = () => getHistory()?.location.pathname ?? window.location.pathname;
const showChunkLoadFailedNotification = () => {
    notification.error({
        key: CHUNK_LOAD_FAILED_NOTIFICATION_KEY,
        message: "Couldn't load this page",
        description: "Please refresh to try again.",
        duration: 0,
        btn: (React.createElement(Button, { type: "primary", size: "small", onClick: () => window.location.reload() }, "Refresh")),
    });
};
/**
 * PD-3751: recovers tabs that keep running an old build after a deploy, on every route. Rendered once in umi's
 * `rootContainer` (outside router context, so the pathname comes from `window.g_history`).
 *
 * - shows the "new version" banner once a newer build is detected, and reloads on the next route change;
 * - a lazy route chunk removed by the deploy (webpack `ChunkLoadError`, an unhandled rejection from dva `dynamic`)
 *   reloads once; if a reload already ran in the last 60s, the user gets a Refresh notification instead of a loop.
 */
const BuildWatcher = () => {
    const { updateAvailable } = useBuildCheck();
    const [pathname, setPathname] = useState(getPathname);
    useReloadOnRouteChange(pathname, updateAvailable);
    useEffect(() => {
        const history = getHistory();
        if (!history)
            return undefined;
        setPathname(history.location.pathname);
        return history.listen(location => setPathname(location.pathname));
    }, []);
    useEffect(() => {
        const recover = (reason) => {
            // a reload is already on its way (route change, or an earlier chunk failure): it covers this failure too
            if (isReloadInProgress() || !isChunkLoadError(reason))
                return;
            // reloading while offline would land on the browser's offline error page
            if (navigator.onLine === false || !reloadOnce())
                showChunkLoadFailedNotification();
        };
        const onUnhandledRejection = (event) => recover(event.reason);
        const onError = (event) => recover(event.error);
        window.addEventListener("unhandledrejection", onUnhandledRejection);
        window.addEventListener("error", onError);
        return () => {
            window.removeEventListener("unhandledrejection", onUnhandledRejection);
            window.removeEventListener("error", onError);
        };
    }, []);
    return React.createElement(UpdateAvailableBanner, { visible: updateAvailable });
};
export default BuildWatcher;
